import {
  AnchorButton,
  Card,
  ManageableItemsList,
  RichTextarea,
  useToastContext,
} from '@payaca/ambrosia';
import { FC, useCallback, useEffect, useMemo, type JSX } from 'react';
import { useDispatch } from 'react-redux';

import FileUploadPersistRemoveControl from '@payaca/components/fileUploadPersistRemoveControl/FileUploadPersistRemoveControl';
import TextareaFieldFormatter, {
  ToolbarColourVariant,
} from '@payaca/components/textareaField/TextareaFieldFormatter';

import useGetMyAccountTerms from '@/api/queries/me/useGetMyAccountTerms';
import { useSelector } from '@/api/state';
import { actions as userActions, actions as usersActions } from '@/api/users';
import { useFormState } from '@/hooks/useFormState';
import CreateEditWrittenTerm from '@/ui/components/companySettings/components/CreateEditWrittenTerm';
import DeleteTermModal from '@/ui/components/companySettings/components/DeleteTermModal';
import { useAccount } from '@/api/rest/users/useAccount';
import { getAcceptedFileTypes } from '@payaca/helpers/fileHelper';
import { FieldValidationResult } from '@payaca/types/fieldValidationTypes';
import { useHistory } from 'react-router';
import { Link, useRouteMatch } from 'react-router-dom';
import CompanySettingsTabBase from './CompanySettingsTabBase';

const acceptFileTypes = getAcceptedFileTypes(['document', 'image']);

type Props = {
  readOnly: boolean;
};

type FormState = {
  businessNotes: string;
  terms: {
    fileName: string;
    identifier: number;
  }[];
};

type FormValidationResult = Partial<
  Record<keyof FormState, FieldValidationResult>
>;

const CompanySettingsTerms: FC<Props> = ({ readOnly }: Props): JSX.Element => {
  const dispatch = useDispatch();

  const history = useHistory();
  const { url } = useRouteMatch();
  const isAddingEditingEmailTemplate = useRouteMatch<{
    emailTemplateId: string;
  }>({
    path: `${url}/:emailTemplateId`,
    exact: true,
  });
  const isDeletingEmailTemplate = useRouteMatch<{
    emailTemplateId: string;
  }>({
    path: `${url}/delete/:emailTemplateId`,
    exact: true,
  });

  const account = useAccount();
  const currentTerms = useSelector((state: any) => state.users.terms);

  const { terms } = useGetMyAccountTerms({
    types: ['WRITTEN'],
  });

  useEffect(() => {
    dispatch(userActions.getAccountTerms(account.id));
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'account.id', and 'dispatch'
  }, []);

  type FormState = {
    businessNotes: string;
  };

  const initialState: FormState = {
    businessNotes: account.businessNotes ?? '',
  };

  const { pushSuccess, pushError } = useToastContext();

  const saveFormData = useMemo(
    () => async (state: FormState) => {
      return new Promise<void>((resolve, reject) => {
        dispatch(
          usersActions.updateBusinessAccount(
            account.id,
            state,
            (err: unknown, response: any) => {
              if (err) {
                pushError(`We couldn't save your changes`);
                // @ts-expect-error
                reject(new Error(err));
              } else {
                pushSuccess('Your changes have been saved');
                resolve();
              }
            }
          )
        );
      });
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has missing dependencies: 'pushSuccess', 'pushError', 'dispatch', and 'account.id'
    []
  );

  const { formState, updateFormFields, formValidationResult } = useFormState<
    FormState,
    FormValidationResult
  >(
    initialState,
    {},
    {
      autoSaveFn: saveFormData,
    }
  );

  const onPersistTerm = useCallback((file: File) => {
    return new Promise<void>((resolve, reject) => {
      dispatch(
        usersActions.uploadTermsDocument(
          account.id,
          {
            file,
            fileName: file.name,
          },
          () => {
            dispatch(userActions.getAccountTerms(account.id));
            resolve();
          }
        )
      );
    });
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'account.id', and 'dispatch'
  }, []);

  const onRemoveTerm = useCallback((fileIdentifier: string) => {
    return new Promise<void>((resolve, reject) => {
      dispatch(
        usersActions.removeTermsDocument(account.id, fileIdentifier, () => {
          dispatch(userActions.getAccountTerms(account.id));
          resolve();
        })
      );
    });
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'account.id', and 'dispatch'
  }, []);

  return (
    <>
      <CompanySettingsTabBase>
        <Card sizeVariant="lg">
          <Card.Body className="flex flex-col gap-4">
            {/* Terms text */}
            <TextareaFieldFormatter
              label="Terms of business"
              value={formState.businessNotes}
              name="businessNotes"
              onChange={updateFormFields}
              isDisabled={readOnly}
              placeholder="Text here will be added to the bottom of every proposal &amp; invoice"
              toolbarColourVariant={ToolbarColourVariant.SECONDARY}
            />

            {/* Terms attachments */}
            <div>
              <FileUploadPersistRemoveControl
                label="Attach a file"
                acceptFileTypes={acceptFileTypes}
                allowMultipleUploads={true}
                enableDragAndDrop={true}
                persistFile={onPersistTerm}
                removePersistedFile={onRemoveTerm}
                persistedFiles={currentTerms.map((x: any) => ({
                  fileName: x.fileName,
                  identifier: x.id,
                  thumbnailUrl: x.termUrl,
                  downloadUrl: x.termUrl,
                }))}
              />
            </div>

            <ManageableItemsList>
              <ManageableItemsList.HeaderBar
                heading="Required terms"
                subHeading="These terms must be accepted by the client before they can sign a proposal"
                buttons={
                  <>
                    <AnchorButton as={Link} size={'sm'} to={`${url}/new`}>
                      Create required term
                    </AnchorButton>
                  </>
                }
              />

              <ManageableItemsList.Table
                items={terms || []}
                uniqueKey={'id'}
                onClickRow={(row) => {
                  history.push(`${url}/${row.id}`);
                }}
                itemActions={[
                  {
                    label: 'Delete',
                    onClick: (item) => {
                      history.push(`${url}/delete/${item.id}`);
                    },
                  },
                ]}
              >
                <ManageableItemsList.Table.Column
                  header=""
                  field="content"
                  render={(content) => {
                    return <RichTextarea value={content} readOnly />;
                  }}
                />
              </ManageableItemsList.Table>
            </ManageableItemsList>
          </Card.Body>
        </Card>
      </CompanySettingsTabBase>

      <CreateEditWrittenTerm
        termId={isAddingEditingEmailTemplate?.params.emailTemplateId}
        isOpen={!!isAddingEditingEmailTemplate}
        onClose={() => {
          history.push(url);
        }}
      />

      <DeleteTermModal
        termId={isDeletingEmailTemplate?.params.emailTemplateId}
        isOpen={!!isDeletingEmailTemplate}
        onClose={() => {
          history.push(url);
        }}
      />
    </>
  );
};

export default CompanySettingsTerms;
